import { HydrateAction, hydrateType } from '@spa/lib/store/HydrateAction';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AuthState } from './AuthState';
import { RegisterFailedCause } from '@spa/lib/store/auth/RegisterFailedCause';
import { SliceActions } from '@spa/lib/store/SliceActions';
import { TitleCode } from '@spa/lib/converters/convertCustomerTitles';

export const initialState: AuthState = {
  accessToken: null,
  authenticatedUserId: null
};

export interface RegisterCustomerRequestedPayload {
  readonly actionId: number;
  readonly email: string;
  readonly titleCode: TitleCode;
  readonly firstName: string;
  readonly lastName: string;
  readonly password: string;
  readonly phoneNumber?: string | null;
}

export interface RegisterCustomerSucceededPayload {
  readonly actionId: number;
  readonly requestedActionId: number;
}

export interface RegisterCustomerFailedPayload {
  readonly actionId: number;
  readonly requestedActionId: number;
  readonly cause: RegisterFailedCause;
}

export interface SignInCustomerRequestedPayload {
  readonly actionId: number;
  readonly email: string;
  readonly password: string;
}

export interface SignInCustomerSucceededPayload {
  readonly actionId: number;
  readonly requestedActionId: number;
}

export interface SignInCustomerFailedPayload {
  readonly actionId: number;
  readonly requestedActionId: number;
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthState(state, { payload }: PayloadAction<AuthState>) {
      state.accessToken = payload.accessToken;
      state.authenticatedUserId = payload.authenticatedUserId;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerCustomerRequested(_, __: PayloadAction<RegisterCustomerRequestedPayload>) {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerCustomerSucceeded(_, __: PayloadAction<RegisterCustomerSucceededPayload>) {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerCustomerFailed(_, __: PayloadAction<RegisterCustomerFailedPayload>) {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    signInCustomerRequested(_, __: PayloadAction<SignInCustomerRequestedPayload>) {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    signInCustomerSucceeded(_, __: PayloadAction<SignInCustomerSucceededPayload>) {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    signInCustomerFailed(_, __: PayloadAction<SignInCustomerFailedPayload>) {},
    logout(state) {
      state.accessToken = null;
      state.authenticatedUserId = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(hydrateType, (state, action: HydrateAction) => {
      if (action.payload.auth) {
        state.accessToken = action.payload.auth.accessToken;
        state.authenticatedUserId = action.payload.auth.authenticatedUserId;
      }
    });
  }
});

export type ActionTypes = SliceActions<typeof authSlice.actions>;

export const {
  registerCustomerSucceeded,
  registerCustomerRequested,
  registerCustomerFailed,
  setAuthState,
  signInCustomerRequested,
  signInCustomerSucceeded,
  signInCustomerFailed,
  logout
} = authSlice.actions;
export default authSlice.reducer;

export const registerCustomerRequestedType = 'auth/registerCustomerRequested';
export type RegisterCustomerRequestedAction = Extract<
  ActionTypes,
  PayloadAction<RegisterCustomerRequestedPayload, typeof registerCustomerRequestedType>
>;

export const registerCustomerSucceededType = 'auth/registerCustomerSucceeded';
export type RegisterCustomerSucceededAction = Extract<
  ActionTypes,
  PayloadAction<RegisterCustomerSucceededPayload, typeof registerCustomerSucceededType>
>;

export const registerCustomerFailedType = 'auth/registerCustomerFailed';
export type RegisterCustomerFailedAction = Extract<
  ActionTypes,
  PayloadAction<RegisterCustomerFailedPayload, typeof registerCustomerFailedType>
>;

export const signInCustomerRequestedType = 'auth/signInCustomerRequested';
export type SignInCustomerRequestedAction = Extract<
  ActionTypes,
  PayloadAction<SignInCustomerRequestedPayload, typeof signInCustomerRequestedType>
>;

export const signInCustomerSucceededType = 'auth/signInCustomerSucceeded';
export type SignInCustomerSucceededAction = Extract<
  ActionTypes,
  PayloadAction<SignInCustomerSucceededPayload, typeof signInCustomerSucceededType>
>;

export const signInCustomerFailedType = 'auth/signInCustomerFailed';
export type SignInCustomerFailedAction = Extract<
  ActionTypes,
  PayloadAction<SignInCustomerFailedPayload, typeof signInCustomerFailedType>
>;
